import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, ShieldCheck } from 'lucide-react';

const TOKENS = {
  lime: '#C7FA3E',
  limeBorder: 'rgba(199, 250, 62, 0.40)',
  surface1: '#15151A',
};

// Trust audit: only citation-backed claims — no blanket clinician attestations.
// "Research cited" badge lists the article's actual evidence sources.
// Inlined here (instead of imported) to side-step a babel-metadata-plugin bug
// that fails on cross-file prop tracing.
const YMYL_CATEGORIES = ['peptides', 'peptide-therapy', 'biohacking', 'longevity', 'supplements', 'health & wellness'];
const YMYL_TAGS = ['peptide', 'bpc-157', 'aod-9604', 'dihexa', 'ghk-cu', 'mk-677', 'retatrutide', 'tb-500', 'mots-c', 'kpv', 'semaglutide', 'nad', 'nmn'];
const YMYL_TITLE_RE = /peptide|bpc.?157|aod.?9604|dihexa|ghk.?cu|mk.?677|retatrutide|tb.?500|semaglutide|longevity|nad\+|nmn/i;

const isYmyl = (article) => {
  if (!article) return false;
  const cat = (article.category || '').toLowerCase();
  if (YMYL_CATEGORIES.includes(cat)) return true;
  if ((article.tags || []).some(t => YMYL_TAGS.includes((t || '').toLowerCase()))) return true;
  return YMYL_TITLE_RE.test(article.title || '');
};

const formatReviewDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch (err) {
    return null;
  }
};

const ReviewerBadge = ({ article }) => {
  if (!isYmyl(article)) return null;
  const sources = (article.evidence_sources && article.evidence_sources.length)
    ? article.evidence_sources.slice(0, 3)
    : [];
  if (!sources.length) return null;
  const reviewedDate = formatReviewDate(article.last_reviewed);
  return (
    <div
      className="inline-flex items-start gap-3 px-4 py-3 my-4"
      style={{ backgroundColor: TOKENS.surface1, border: `1px solid ${TOKENS.limeBorder}` }}
      data-testid="medical-reviewer-badge"
    >
      <ShieldCheck className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: TOKENS.lime }} strokeWidth={1.5} />
      <div className="min-w-0">
        <p
          className="text-[10px] uppercase tracking-[0.2em] mb-1"
          style={{ color: TOKENS.lime, fontFamily: 'Anton, Archivo Narrow, Bebas Neue, sans-serif', letterSpacing: '0.2em' }}
        >
          Research cited
        </p>
        <p className="text-white text-sm leading-tight" data-testid="evidence-sources-line">
          Peer-reviewed research from <span className="text-zinc-300">{sources.join(' · ')}</span>
        </p>
        {reviewedDate && (
          <p className="text-zinc-500 text-xs mt-1 inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Last reviewed: <time>{reviewedDate}</time>
          </p>
        )}
      </div>
    </div>
  );
};

export const ArticleHero = ({ article }) => (
  <div className="relative h-[50vh] overflow-hidden">
    {article.featured_video ? (
      <video
        src={article.featured_video}
        poster={article.featured_video.replace(/\.[^.]+$/, '-poster.jpg')}
        autoPlay
        loop
        muted
        playsInline
        className="w-full h-full object-cover"
        data-testid="article-hero-video"
      />
    ) : (
      <img
        src={article.featured_image}
        alt={article.title}
        className="w-full h-full object-cover"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        data-testid="article-hero-image"
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/50 to-transparent" />
  </div>
);

export const ArticleHeader = ({ article }) => (
  <header className="max-w-3xl mb-12">
    <span
      className="inline-block px-3 py-1 text-xs uppercase tracking-wider text-white mb-4"
      style={{
        backgroundColor: article.category === 'HYROX' ? '#FFD100' : '#E63946',
        color: article.category === 'HYROX' ? '#000' : '#fff',
      }}
    >
      {article.category}
    </span>
    <h1
      className="text-4xl md:text-5xl lg:text-6xl text-white mb-6"
      style={{ fontFamily: 'Bebas Neue, sans-serif' }}
      data-testid="article-title"
    >
      {article.title}
    </h1>
    <p className="text-xl text-zinc-400 mb-6">{article.excerpt}</p>
    <div className="flex flex-wrap items-center gap-6 text-zinc-500 text-sm">
      {article.read_time && (
        <span className="flex items-center gap-2">
          <Clock className="w-4 h-4" /> {article.read_time} min read
        </span>
      )}
      {article.created_at && (
        <span className="flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          {new Date(article.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </span>
      )}
    </div>
    {article.reviewed_by && (
      <p className="flex items-center gap-2 text-zinc-400 text-sm mt-4" data-testid="expert-reviewer-line">
        <ShieldCheck className="w-4 h-4 flex-shrink-0" style={{ color: TOKENS.lime }} strokeWidth={1.5} />
        <span>
          Reviewed by <span className="text-white font-medium">{article.reviewed_by}</span>
          {article.reviewer_credentials && (
            <span className="text-zinc-500"> — {article.reviewer_credentials}</span>
          )}
        </span>
      </p>
    )}
    <ReviewerBadge article={article} />
  </header>
);

export const BackToBlogLink = () => (
  <Link
    to="/blog"
    className="inline-flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider hover:text-white transition-colors duration-300 mb-8"
    style={{ fontFamily: 'Bebas Neue, sans-serif' }}
    data-testid="back-to-blog"
  >
    <ArrowLeft className="w-3 h-3" /> Back to All Articles
  </Link>
);
